import { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
// eslint-disable-next-line no-unused-vars
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ArrowRight, Loader2, Grid3X3 } from 'lucide-react';
import { ProgressSteps } from '@/components/ProgressSteps';
import { AppShell3 } from '@/components/app-shell-3';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useAuth } from '@/contexts/AuthContext';
import { extractMappedData } from './utils/parseSpreadsheet';
import { UploadStep } from './components/UploadStep';
import { MappingStep } from './components/MappingStep';
import { UsersReviewStep } from './components/UsersReviewStep';
import { QualificationsReviewStep } from './components/QualificationsReviewStep';
import { AssignmentsReviewStep } from './components/AssignmentsReviewStep';
import { ConfirmImportStep } from './components/ConfirmImportStep';

const progressSteps = [
  { title: 'Upload', description: 'Select file' },
  { title: 'Map Data', description: 'Configure columns' },
  { title: 'Users', description: 'Match people' },
  { title: 'Qualifications', description: 'Match courses' },
  { title: 'Assignments', description: 'Review data' },
  { title: 'Import', description: 'Confirm & run' }
];

const ImportMatrixPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { loading: authLoading, permissionsLoaded } = useAuth();
  usePageTitle('Import Matrix');

  const [currentStep, setCurrentStep] = useState(1);
  const [animationDirection, setAnimationDirection] = useState(0);

  // Step 1: File data
  const [fileData, setFileData] = useState(null);

  // Step 2: Mapping
  const [mapping, setMapping] = useState(null);

  // Step 3: User matches
  const [userMatches, setUserMatches] = useState([]);

  // Step 4: Qualification matches
  const [qualMatches, setQualMatches] = useState([]);

  // Step 5: Assignment config + parsed assignments
  const [assignmentConfig, setAssignmentConfig] = useState({
    dateFormat: 'DD/MM/YYYY',
    dateInterpretation: 'auto',
  });
  const [parsedAssignments, setParsedAssignments] = useState([]);

  // Step 6: Import complete
  const [importComplete, setImportComplete] = useState(false);

  // Extract mapped data when transitioning from step 2 to step 3
  const getMappedData = useCallback(() => {
    if (!fileData?.sheetData || !mapping) return { users: [], qualifications: [], cells: [] };
    return extractMappedData(fileData.sheetData, mapping);
  }, [fileData?.sheetData, mapping]);

  const canProceed = () => {
    switch (currentStep) {
      case 1: return !!fileData?.sheetData;
      case 2: return mapping && mapping.headerRow !== null && mapping.nameColumn !== null;
      case 3: return userMatches.length > 0;
      case 4: return qualMatches.length > 0;
      case 5: return parsedAssignments.length >= 0; // Allow zero - user may want to just create users/quals
      case 6: return true;
      default: return false;
    }
  };

  const handleNext = () => {
    if (currentStep >= 6 || !canProceed()) return;
    setAnimationDirection(1);

    // When leaving step 2, extract data for step 3
    if (currentStep === 2) {
      const mapped = getMappedData();
      // Reset matches when mapping changes
      if (userMatches.length > 0) {
        // Only reset if mapping actually changed
      }
      // Store parsed users/quals for the review steps
      if (mapped.users.length === 0) return; // Don't proceed with no users
    }

    setCurrentStep(currentStep + 1);
  };

  const handlePrevious = () => {
    if (currentStep <= 1) return;
    setAnimationDirection(-1);
    setCurrentStep(currentStep - 1);
  };

  const handleImportComplete = () => {
    setImportComplete(true);
  };

  const handleViewMatrix = () => {
    navigate(`/space/${projectId}/matrix`);
  };

  const authReady = !authLoading && permissionsLoaded;

  if (!authReady) {
    return (
      <AppShell3 breadcrumbs={[]} pageTitle="" pageDescription="">
        <div className="min-h-[calc(100vh-200px)] flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-violet-600" />
        </div>
      </AppShell3>
    );
  }

  // Get mapped data for child steps
  const mappedData = fileData?.sheetData && mapping ? getMappedData() : { users: [], qualifications: [], cells: [] };

  return (
    <AppShell3 breadcrumbs={[]} pageTitle="" pageDescription="">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-xl shadow-xl border overflow-hidden"
        >
          {/* Header */}
          <div className="relative bg-gradient-to-br from-gray-50 to-white border-b px-8 py-4">
            <div className="absolute top-4 left-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(`/space/${projectId}/matrix`)}
                className="gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Matrix
              </Button>
            </div>

            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-4">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Import Training Matrix</h1>
                <p className="text-gray-600">Upload a spreadsheet to bulk-import users, qualifications, and assignments</p>
              </div>
              <ProgressSteps steps={progressSteps} currentStep={currentStep} />
            </div>
          </div>

          {/* Content */}
          <div className="px-8 py-8">
            <AnimatePresence mode="wait">
              {currentStep === 1 && (
                <StepWrapper key="step1" direction={animationDirection}>
                  <UploadStep
                    fileData={fileData}
                    setFileData={setFileData}
                  />
                </StepWrapper>
              )}

              {currentStep === 2 && (
                <StepWrapper key="step2" direction={animationDirection}>
                  <MappingStep
                    sheetData={fileData?.sheetData}
                    mapping={mapping}
                    setMapping={setMapping}
                  />
                </StepWrapper>
              )}

              {currentStep === 3 && (
                <StepWrapper key="step3" direction={animationDirection}>
                  <UsersReviewStep
                    parsedUsers={mappedData.users}
                    userMatches={userMatches}
                    setUserMatches={setUserMatches}
                  />
                </StepWrapper>
              )}

              {currentStep === 4 && (
                <StepWrapper key="step4" direction={animationDirection}>
                  <QualificationsReviewStep
                    parsedQualifications={mappedData.qualifications}
                    projectId={projectId}
                    qualMatches={qualMatches}
                    setQualMatches={setQualMatches}
                  />
                </StepWrapper>
              )}

              {currentStep === 5 && (
                <StepWrapper key="step5" direction={animationDirection}>
                  <AssignmentsReviewStep
                    sheetData={fileData?.sheetData}
                    mapping={mapping}
                    userMatches={userMatches}
                    qualMatches={qualMatches}
                    assignmentConfig={assignmentConfig}
                    setAssignmentConfig={setAssignmentConfig}
                    parsedAssignments={parsedAssignments}
                    setParsedAssignments={setParsedAssignments}
                  />
                </StepWrapper>
              )}

              {currentStep === 6 && (
                <StepWrapper key="step6" direction={animationDirection}>
                  <ConfirmImportStep
                    userMatches={userMatches}
                    qualMatches={qualMatches}
                    parsedAssignments={parsedAssignments}
                    projectId={projectId}
                    onComplete={handleImportComplete}
                  />
                </StepWrapper>
              )}
            </AnimatePresence>
          </div>

          {/* Footer Navigation */}
          <div className="px-8 py-4 bg-gray-50 border-t flex justify-between">
            <div>
              {currentStep > 1 && !importComplete && (
                <Button
                  variant="outline"
                  onClick={handlePrevious}
                  className="flex items-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Previous
                </Button>
              )}
            </div>

            <div>
              {importComplete ? (
                <Button
                  onClick={handleViewMatrix}
                  className="bg-black hover:bg-gray-900 text-white flex items-center gap-2"
                >
                  <Grid3X3 className="w-4 h-4" />
                  View Matrix
                </Button>
              ) : currentStep < 6 ? (
                <Button
                  onClick={handleNext}
                  className="bg-black hover:bg-gray-900 text-white flex items-center gap-2"
                  disabled={!canProceed()}
                >
                  Next
                  <ArrowRight className="w-4 h-4" />
                </Button>
              ) : null}
            </div>
          </div>
        </motion.div>
      </div>
    </AppShell3>
  );
};

function StepWrapper({ children, direction }) {
  return (
    <motion.div
      initial={{
        opacity: 0,
        x: direction === 0 ? 0 : direction === 1 ? 100 : -100
      }}
      animate={{ opacity: 1, x: 0 }}
      exit={{
        opacity: 0,
        x: direction === 1 ? -100 : 100
      }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
    >
      {children}
    </motion.div>
  );
}

export default ImportMatrixPage;
